/**
 * /ap/autopilot — Entity-wide AP Autopilot dashboard.
 *
 * Shows:
 *   - Entity-level summary: total bills processed, auto-posted, no-touch rate
 *   - Per-vendor table: mode, threshold, 90-day stats, readiness status
 *   - Quick links to each vendor's Autopilot settings tab
 *
 * Data sources:
 *   GET /api/ap/intake/autopilot/readiness  — P3 readiness per vendor
 *   GET /api/ap/intake/autopilot/stats      — entity-level aggregate (via summary)
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CircularProgress } from "@mui/material";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/ap/autopilot")({
  component: AutopilotDashboardPage,
});

// ─── types ───────────────────────────────────────────────────────────────────

interface ReadinessItem {
  vendor_id: string;
  sample_size: number;
  recommended_threshold: string | null;
  ready_for_live: boolean;
  no_touch_rate: string | null;
  wrong_post_count: number;
  agreement_rate: string;
  explanation: string;
}

interface EntityReadiness {
  items: ReadinessItem[];
  total: number;
  ready_count: number;
  lookback_days: number;
}

interface VendorPref {
  vendor_id: string;
  auto_post_mode: string;
  auto_post_threshold: string | null;
  auto_post_ceiling: string | null;
  vendor_watch_slug: string | null;
}

interface VendorStats {
  total_processed: number;
  auto_posted_count: number;
  auto_post_rate: number;
}

// Minimal vendor info from parties list
interface PartySummary {
  id: string;
  name: string | null;
}

// ─── page ────────────────────────────────────────────────────────────────────

function AutopilotDashboardPage() {
  const readinessQ = useQuery({
    queryKey: ["entity-autopilot-readiness"],
    queryFn: () => api<EntityReadiness>("/api/ap/intake/autopilot/readiness?lookback_days=180"),
    staleTime: 2 * 60_000,
  });

  const summaryQ = useQuery({
    queryKey: ["entity-autopilot-summary"],
    queryFn: () => api<{
      entity_id: string;
      period_days: number;
      total_intakes: number;
      auto_posted_count: number;
      auto_post_rate: number;
      shadow_mode_count: number;
      live_mode_count: number;
    }>("/api/ap/intake/autopilot/summary"),
    staleTime: 2 * 60_000,
  });

  const partiesQ = useQuery({
    queryKey: ["parties-light"],
    queryFn: () => api<{ items: PartySummary[] }>("/api/parties?limit=500"),
    staleTime: 10 * 60_000,
  });

  const partiesById = Object.fromEntries(
    (partiesQ.data?.items ?? []).map((p) => [p.id, p])
  );

  const items = readinessQ.data?.items ?? [];
  const liveVendors = items.filter((i) => {
    // We don't have the pref mode here, but ready_for_live + sample_size hints at it
    return i.ready_for_live;
  });

  const summary = summaryQ.data;

  return (
    <div className="flex flex-col min-h-full bg-slate-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              AP Autopilot
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              No-touch invoice posting — configure per vendor, monitor readiness
            </p>
          </div>
          {(readinessQ.isLoading || summaryQ.isLoading) && (
            <CircularProgress size={18} />
          )}
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-5xl">
        {/* Entity-level summary cards */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <SummaryCard
              label="Bills processed"
              value={summary.total_intakes}
              sub={`last ${summary.period_days}d`}
            />
            <SummaryCard
              label="Auto-posted"
              value={summary.auto_posted_count}
              highlight={summary.auto_posted_count > 0}
            />
            <SummaryCard
              label="No-touch rate"
              value={`${summary.auto_post_rate}%`}
              highlight={summary.auto_post_rate > 0}
            />
            <SummaryCard
              label="Live vendors"
              value={summary.live_mode_count}
              sub={`${summary.shadow_mode_count} in shadow`}
              highlight={summary.live_mode_count > 0}
            />
          </div>
        )}

        {/* Readiness table */}
        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 dark:border-gray-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Vendor readiness
            </h2>
            {readinessQ.data && (
              <span className="text-xs text-slate-400 dark:text-slate-500">
                {readinessQ.data.ready_count} of {readinessQ.data.total} ready for live ·{" "}
                {readinessQ.data.lookback_days}d lookback
              </span>
            )}
          </div>

          {readinessQ.isError && (
            <div className="p-5 text-sm text-red-600 dark:text-red-400">
              {(readinessQ.error as Error).message}
            </div>
          )}

          {items.length === 0 && !readinessQ.isLoading && (
            <div className="p-8 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No vendors have decided intake history yet.
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Enable Shadow mode on a vendor's Autopilot tab — once 30+ bills are
                reviewed by your accountant, readiness data appears here.
              </p>
              <Link
                to="/vendors"
                className="inline-block mt-3 text-xs text-blue-600 dark:text-blue-400 underline"
              >
                Go to Vendors →
              </Link>
            </div>
          )}

          {items.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-gray-800">
                  <th className="text-left px-5 py-2.5 font-medium">Vendor</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Bills analyzed</th>
                  <th className="text-right px-4 py-2.5 font-medium">Agreement</th>
                  <th className="text-right px-4 py-2.5 font-medium">No-touch rate</th>
                  <th className="text-right px-4 py-2.5 font-medium">Wrong posts</th>
                  <th className="text-right px-4 py-2.5 font-medium">Rec. threshold</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const vendor = partiesById[item.vendor_id];
                  const vendorName = vendor?.name ?? item.vendor_id.slice(0, 8) + "…";
                  const agreementPct = Math.round(parseFloat(item.agreement_rate) * 100);
                  const noTouchPct = item.no_touch_rate
                    ? Math.round(parseFloat(item.no_touch_rate) * 100)
                    : null;

                  return (
                    <tr
                      key={item.vendor_id}
                      className="border-b border-slate-50 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-5 py-3 font-medium text-slate-700 dark:text-slate-300">
                        {vendorName}
                      </td>
                      <td className="px-4 py-3">
                        <ReadinessBadge ready={item.ready_for_live} sampleSize={item.sample_size} />
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400">
                        {item.sample_size}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={agreementPct >= 90 ? "text-emerald-600 dark:text-emerald-400 font-medium" : "text-slate-600 dark:text-slate-400"}>
                          {agreementPct}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400">
                        {noTouchPct !== null ? `${noTouchPct}%` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={item.wrong_post_count > 0 ? "text-red-600 dark:text-red-400 font-medium" : "text-slate-400 dark:text-slate-500"}>
                          {item.wrong_post_count}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-slate-600 dark:text-slate-400">
                        {item.recommended_threshold ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to="/vendors/$vendorId/autopilot"
                          params={{ vendorId: item.vendor_id }}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                        >
                          Configure →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* How autopilot works — inline explainer for first-time visitors */}
        {items.length === 0 && !readinessQ.isLoading && (
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-5 text-sm text-blue-800 dark:text-blue-200 space-y-2">
            <p className="font-semibold">How to get started</p>
            <ol className="list-decimal list-inside space-y-1 text-xs leading-relaxed">
              <li>Go to <Link to="/vendors" className="underline">Vendors</Link>, pick a recurring SaaS vendor (Anthropic, AWS, Stripe…)</li>
              <li>Open the <strong>Autopilot</strong> tab and set a confidence threshold (start at 0.90)</li>
              <li>Select the matching <strong>Vendor-watch</strong> slug if you have spend monitoring running</li>
              <li>Leave mode as <strong>Shadow</strong> — autopilot logs what it would do without posting</li>
              <li>After 30+ bills are reviewed by your team, come back here — the Readiness card will tell you if you're ready to go live</li>
            </ol>
            <p className="text-xs text-blue-600 dark:text-blue-300 pt-1">
              Target metric: "I haven't manually touched an Anthropic or AWS bill in 3 months."
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── micro components ─────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string | number;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-4">
      <div className={`text-2xl font-semibold ${highlight ? "text-blue-600 dark:text-blue-400" : "text-slate-700 dark:text-slate-300"}`}>
        {value}
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ReadinessBadge({ ready, sampleSize }: { ready: boolean; sampleSize: number }) {
  if (ready) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
        Ready for live
      </span>
    );
  }
  if (sampleSize >= 10) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
        Building history
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-slate-400">
      Needs data
    </span>
  );
}
